import {
  BarChart3,
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  Globe2,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
  Puzzle,
  Receipt,
  Rocket,
  Settings,
  Smartphone,
  Video,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Badge } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/format";
import { NavLink, useLocation } from "react-router-dom";
import { OPERA_BRAND } from "@/assets/branding";

export interface NavChild {
  id: string;
  label: string;
  to: string;
  children?: Array<{ id: string; label: string; to: string }>;
}

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
  badge?: string | number;
  badgeTone?: "primary" | "success" | "warning";
  children?: NavChild[];
}

interface NavSection {
  id: string;
  label?: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    id: "main",
    items: [{ id: "dashboard", label: "Dashboard", to: "/", icon: LayoutDashboard }],
  },
  {
    id: "business",
    label: "Business",
    items: [
      {
        id: "bd",
        label: "Business Development",
        to: "/business-development",
        icon: BriefcaseBusiness,
        badge: 8,
        badgeTone: "primary",
        children: [
          { id: "bd-dash", label: "Dashboard", to: "/business-development" },
          { id: "bd-inq", label: "Inquiries", to: "/business-development/inquiries" },
          {
            id: "bd-app",
            label: "Applications",
            to: "/business-development/applications",
          },
          {
            id: "bd-announcements",
            label: "Announcements",
            to: "/business-development/announcements",
          },
          { id: "bd-crm", label: "CRM", to: "/business-development/crm" },
          { id: "bd-tasks", label: "Tasks", to: "/business-development/tasks" },
          {
            id: "bd-docs",
            label: "Sales Documents",
            to: "/business-development/documents",
            children: [
              {
                id: "bd-calc",
                label: "Cost Calculator",
                to: "/business-development/documents/calculator",
              },
              {
                id: "bd-pricing-lib",
                label: "Pricing Library",
                to: "/business-development/documents/pricing-library",
              },
              {
                id: "bd-quo",
                label: "Quotations",
                to: "/business-development/documents/quotations",
              },
              {
                id: "bd-prop",
                label: "Proposals",
                to: "/business-development/documents/proposals",
              },
              {
                id: "bd-ctr",
                label: "Contracts",
                to: "/business-development/documents/contracts",
              },
              {
                id: "bd-tpl",
                label: "Templates",
                to: "/business-development/documents/templates",
              },
            ],
          },
          { id: "bd-reports", label: "Reports", to: "/business-development/reports" },
        ],
      },
    ],
  },
  {
    id: "meetings",
    label: "Meetings",
    items: [
      {
        id: "meetings",
        label: "Meetings",
        to: "/meetings",
        icon: Video,
        children: [
          { id: "meet-dash", label: "Dashboard", to: "/meetings" },
          { id: "meet-cal", label: "Calendar", to: "/meetings/calendar" },
          { id: "meet-upcoming", label: "Upcoming", to: "/meetings/upcoming" },
          { id: "meet-internal", label: "Internal", to: "/meetings/internal" },
          { id: "meet-clients", label: "Client Meetings", to: "/meetings/clients" },
          { id: "meet-sales", label: "Sales", to: "/meetings/sales" },
          { id: "meet-projects", label: "Projects", to: "/meetings/projects" },
          { id: "meet-interviews", label: "Interviews", to: "/meetings/interviews" },
          { id: "meet-notes", label: "Notes", to: "/meetings/notes" },
          { id: "meet-recordings", label: "Recordings", to: "/meetings/recordings" },
          { id: "meet-followups", label: "Follow-ups", to: "/meetings/follow-ups" },
          { id: "meet-actions", label: "Action Items", to: "/meetings/action-items" },
          { id: "meet-templates", label: "Templates", to: "/meetings/templates" },
          { id: "meet-analytics", label: "Analytics", to: "/meetings/analytics" },
        ],
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      {
        id: "finance",
        label: "Finance",
        to: "/finance",
        icon: Receipt,
        badge: 2,
        badgeTone: "warning",
        children: [
          { id: "fin-dash", label: "Dashboard", to: "/finance" },
          { id: "fin-income", label: "Income", to: "/finance/income" },
          { id: "fin-expenses", label: "Expenses", to: "/finance/expenses" },
          { id: "fin-invoices", label: "Invoices", to: "/finance/invoices" },
          {
            id: "fin-transactions",
            label: "Transactions",
            to: "/finance/transactions",
          },
          { id: "fin-cash", label: "Cash Flow", to: "/finance/cash-flow" },
          { id: "fin-reports", label: "Reports", to: "/finance/reports" },
          { id: "fin-settings", label: "Settings", to: "/finance/settings" },
        ],
      },
    ],
  },
  {
    id: "sales-engine",
    label: "Sales Engine",
    items: [
      {
        id: "sales-engine",
        label: "Sales Engine",
        to: "/sales-engine",
        icon: Rocket,
        children: [
          { id: "se-dash", label: "Dashboard", to: "/sales-engine" },
          { id: "se-audience", label: "Audience", to: "/sales-engine/audience" },
          { id: "se-templates", label: "Templates", to: "/sales-engine/templates" },
          { id: "se-campaigns", label: "Campaigns", to: "/sales-engine/campaigns" },
          {
            id: "se-successful",
            label: "Successful Leads",
            to: "/sales-engine/successful-leads",
          },
          { id: "se-dead", label: "Dead Leads", to: "/sales-engine/dead-leads" },
          { id: "se-analytics", label: "Analytics", to: "/sales-engine/analytics" },
        ],
      },
    ],
  },
  {
    id: "website",
    label: "Website",
    items: [
      {
        id: "cms",
        label: "Website CMS",
        to: "/cms",
        icon: Globe2,
        badge: "New",
        badgeTone: "success",
        children: [
          { id: "cms-dash", label: "Dashboard", to: "/cms" },
          { id: "cms-home", label: "Homepage", to: "/cms/homepage" },
          { id: "cms-portfolio", label: "Portfolio", to: "/cms/portfolio" },
          { id: "cms-founder", label: "Founder", to: "/cms/founder" },
          { id: "cms-company", label: "Company Information", to: "/cms/company" },
          { id: "cms-social", label: "Social Media", to: "/cms/social" },
          { id: "cms-analytics", label: "Visitor Analytics", to: "/cms/analytics" },
        ],
      },
    ],
  },
  {
    id: "growth",
    label: "Strategy",
    items: [
      { id: "growth-strategy", label: "Growth Strategy Center", to: "/growth-strategy", icon: BarChart3 },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    items: [
      { id: "integrations", label: "Integration Hub", to: "/integrations", icon: Puzzle },
    ],
  },
  {
    id: "system",
    label: "System",
    items: [
      {
        id: "settings",
        label: "System Settings",
        to: "/system-settings",
        icon: Settings,
      },
      {
        id: "download-app",
        label: "Get OPERA OS App",
        to: "/download-app",
        icon: Smartphone,
      },
    ],
  },
];

export const primaryNav: NavItem[] = navSections.flatMap((s) => s.items);

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const Icon = item.icon;
  const active =
    item.to === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.to);
  const hasChildren = Boolean(item.children?.length);

  useEffect(() => {
    if (active && hasChildren) setExpanded(true);
  }, [active, hasChildren]);

  return (
    <div className="sidebar__item">
      <div className="sidebar__row">
        <NavLink
          to={item.to}
          title={collapsed ? item.label : undefined}
          className={cn("sidebar__link", active && "sidebar__link--active")}
          onClick={onNavigate}
        >
          <span className="sidebar__link-icon">
            <Icon size={18} strokeWidth={1.85} aria-hidden />
          </span>
          <span className="sidebar__label">{item.label}</span>
          {item.badge != null ? (
            <span className="sidebar__link-badge">
              <Badge tone={item.badgeTone ?? "primary"}>{item.badge}</Badge>
            </span>
          ) : null}
        </NavLink>
        {hasChildren && !collapsed ? (
          <button
            type="button"
            className="sidebar__expand"
            aria-label={expanded ? "Collapse section" : "Expand section"}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        ) : null}
      </div>
      {hasChildren && expanded && !collapsed ? (
        <div className="sidebar__children">
          {item.children!.map((child) => (
            <div key={child.id} className="sidebar__child-block">
              <NavLink
                to={child.to}
                end={child.to === item.to}
                className={({ isActive }) =>
                  cn("sidebar__child", isActive && "sidebar__child--active")
                }
                onClick={onNavigate}
              >
                {child.label}
              </NavLink>
              {child.children?.length ? (
                <div className="sidebar__grandchildren">
                  {child.children.map((gc) => (
                    <NavLink
                      key={gc.id}
                      to={gc.to}
                      className={({ isActive }) =>
                        cn("sidebar__grandchild", isActive && "sidebar__grandchild--active")
                      }
                      onClick={onNavigate}
                    >
                      {gc.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapse,
  onNavigate,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onNavigate?: () => void;
}) {
  const sections = useMemo(() => navSections, []);

  return (
    <aside className={cn("sidebar", collapsed && "sidebar--collapsed")} aria-label="Primary">
      <div className="sidebar__brand">
        <img
          src={OPERA_BRAND.full}
          alt={OPERA_BRAND.name}
          className={cn("sidebar__brand-logo", collapsed && "sidebar__brand-logo--collapsed")}
          decoding="async"
        />
      </div>

      <nav className="sidebar__nav">
        {sections.map((section) => (
          <div key={section.id} className="sidebar__section">
            {section.label && !collapsed ? (
              <div className="sidebar__section-label">{section.label}</div>
            ) : null}
            {section.items.map((item) => (
              <NavRow
                key={item.id}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar__footer">
        <Button
          variant="ghost"
          className="sidebar__collapse-btn"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          <span className="sidebar__label">{collapsed ? "Expand Sidebar" : "Collapse Sidebar"}</span>
        </Button>
      </div>
    </aside>
  );
}

export function SidebarSectionSlot({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
